import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'shared/ui/error-boundary';
import { ToggleTranslateButton } from 'shared/ui/toggle-translate-button';
import ImageBackground from 'shared/assets/images/bg-image-login.png';
import ImageLogo from 'shared/assets/images/logo_sembuh_ai.png';
import { PasswordInputField } from '../components/password-input-field';
import { SuccessModal } from '../components/success-modal';
import useNewPassword from '../hooks/useNewPassword';

const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-t-2 mx-3 my-0.5 border-white border-solid m-auto" />
);

const NewPasswordPage = () => {
  const { t } = useTranslation('common');
  const {
    showPassword,
    showPasswordConfirm,
    loading,
    isModalOpen,
    formData,
    errors,
    handleChange,
    handleTogglePassword,
    handleTogglePasswordConfirm,
    handleResetPassword,
    redirectLogin,
    passwordTooltip,
  } = useNewPassword();

  return (
    <ErrorBoundary>
      <main
        className="w-full content-center h-screen flex flex-col items-center md:bg-slate-100"
        style={{
          backgroundImage: `url(${ImageBackground})`,
          backgroundSize: '100% 100%',
        }}
      >
        <div className="flex items-center justify-center h-full w-full mt-[-24px]">
          <div className="w-1/4 flex flex-wrap p-8 rounded-2xl bg-white justify-center items-center text-center">
            <button
              type="button"
              onClick={redirectLogin}
              className="w-full contents cursor-pointer"
            >
              <img src={ImageLogo} alt="logo_sembuh_ai" className="sl-w-auto" />
            </button>
            <div className="w-full mt-5">
              <span className="font-semibold text-2xl">
                {t('set_new_password')}
              </span>
            </div>
            <div className="w-full">
              <span className="text-base font-normal text-slate-500">
                {t('enter_new_password')}
              </span>
            </div>

            {errors?.error && (
              <div className="w-full rounded-lg py-3 px-4 bg-red-50 border-2 border-[#FECDCA] mt-6 text-[#B42318] font-medium text-sm text-start">
                {errors?.error}
              </div>
            )}

            <div className="w-full my-3">
              <PasswordInputField
                name="password"
                placeholder={t('new_password')}
                value={formData?.password}
                onChange={handleChange}
                showPassword={showPassword}
                onToggle={handleTogglePassword}
                error={errors?.password}
                tooltip={passwordTooltip}
              />
            </div>

            <div className="w-full">
              <PasswordInputField
                name="password_confirmation"
                placeholder={t('confirm_new_password')}
                value={formData?.password_confirmation}
                onChange={handleChange}
                showPassword={showPasswordConfirm}
                onToggle={handleTogglePasswordConfirm}
                error={errors?.password_confirmation}
              />
            </div>

            <div className="w-full flex flex-col justify-center text-center mt-6 gap-4">
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={!!loading || formData?.password_confirmation === ''}
                className={`flex w-full ${
                  formData?.password_confirmation === ''
                    ? 'bg-blue-300'
                    : 'bg-blue-500'
                } justify-center rounded-md bg-con-brand px-3 py-2 text-medium font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
              >
                {loading ? <Spinner /> : t('reset_password')}
              </button>
              <div className="flex justify-between items-center gap-1">
                <p>{t('select_language')}</p>
                <ToggleTranslateButton />
              </div>
            </div>
          </div>
        </div>
      </main>

      <SuccessModal isOpen={isModalOpen} onContinue={redirectLogin} />
    </ErrorBoundary>
  );
};

export default NewPasswordPage;
